import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import {
    Container, Typography, Paper, Button, Box, CircularProgress, Chip,
    Avatar, Alert,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';
import VerifiedIcon from '@mui/icons-material/Verified';
import PaymentsIcon from '@mui/icons-material/Payments';
import DescriptionIcon from '@mui/icons-material/Description';
import moment from 'jalali-moment';
import { CONTRACT_LABELS, SHIFT_LABELS, REQUEST_STATUS_LABELS, formatToman } from './labels';
import { glass } from './theme';

function RequesterDashboard() {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [payMessage, setPayMessage] = useState('');
    const [contract, setContract] = useState(null);

    useEffect(() => {
        apiClient.get('/api/dashboard/requester/')
            .then((response) => setDashboardData(response.data))
            .catch((error) => console.error(error))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const latestId = dashboardData?.latest_request?.id;
        const hasAcceptedBid = dashboardData?.latest_request?.bids?.some((b) => b.status === 'accepted');
        if (latestId && hasAcceptedBid) {
            apiClient.get(`/api/contracts/request/${latestId}/`)
                .then((res) => setContract(res.data))
                .catch(() => setContract(null));
        }
    }, [dashboardData]);

    const handlePay = async (amount, requestId) => {
        setPaying(true);
        setPayMessage('');
        try {
            const res = await apiClient.post('/api/payments/request/', {
                amount,
                description: `پرداخت بابت درخواست شماره ${requestId}`,
                request_id: requestId,
            });
            window.location.href = res.data.pay_url;
        } catch (error) {
            if (error.response?.status === 501) {
                setPayMessage('درگاه پرداخت به‌زودی فعال می‌شود — به‌محض فعال‌سازی، پرداخت آنلاین از همین‌جا ممکن خواهد بود.');
            } else {
                setPayMessage('خطا در اتصال به درگاه پرداخت. لطفاً دوباره تلاش کنید.');
            }
        } finally {
            setPaying(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    const latest = dashboardData?.latest_request;
    const acceptedBid = latest?.bids?.find((b) => b.status === 'accepted');
    const pendingBidsCount = latest?.bids?.filter((b) => b.status === 'submitted').length || 0;

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
        <Container maxWidth="lg" sx={{ pt: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 800 }} gutterBottom>داشبورد خانواده</Typography>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                خوش آمدید، {dashboardData?.full_name || 'کاربر گرامی'}
            </Typography>

            <Box sx={{ ...glass.card, p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>وضعیت آخرین درخواست شما</Typography>

                {latest ? (
                    <Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                            <Typography sx={{ fontWeight: 700 }}>درخواست شماره #{latest.id}</Typography>
                            <Chip
                                label={REQUEST_STATUS_LABELS[latest.status] || latest.status}
                                color={latest.status === 'active' ? 'primary' : 'default'}
                                size="small"
                            />
                            <Chip size="small" variant="outlined" label={CONTRACT_LABELS[latest.contract_type] || latest.contract_type} />
                            <Chip size="small" variant="outlined" label={SHIFT_LABELS[latest.shift_type] || latest.shift_type} />
                            <Chip size="small" variant="outlined" label={moment(latest.start_date).locale('fa').format('D MMMM YYYY')} />
                        </Box>

                        {acceptedBid ? (
                            <Paper variant="outlined" sx={{ p: 2.25, bgcolor: 'primary.light', mb: 2 }}>
                                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>همیار انتخاب‌شده</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                                    <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', width: 48, height: 48, fontWeight: 800 }}>
                                        {(acceptedBid.provider?.full_name || acceptedBid.provider?.user?.first_name || 'ه')[0]}
                                    </Avatar>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography sx={{ fontWeight: 700 }}>
                                                {acceptedBid.provider?.full_name || 'همیار'}
                                            </Typography>
                                            {acceptedBid.provider?.is_verified_by_admin && (
                                                <VerifiedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                            )}
                                        </Box>
                                        <Typography variant="body2" color="text.secondary">
                                            امتیاز {Number(acceptedBid.provider?.average_rating || 0).toFixed(1)} از ۵ ·{' '}
                                            {acceptedBid.provider?.work_experience_years || 0} سال سابقه
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'end' }}>
                                        <Typography sx={{ fontWeight: 800 }}>{formatToman(acceptedBid.amount)}</Typography>
                                        <Button
                                            size="small" variant="outlined"
                                            startIcon={<DescriptionIcon />}
                                            component={RouterLink}
                                            to={`/contracts/${latest.id}`}
                                            sx={{ mt: 0.5, display: 'block', mb: 0.75 }}
                                        >
                                            {contract?.is_fully_signed ? 'مشاهده‌ی قرارداد' : 'مشاهده و پذیرش قرارداد'}
                                        </Button>
                                        <Button
                                            size="small" variant="contained" color="secondary"
                                            startIcon={paying ? <CircularProgress size={16} color="inherit" /> : <PaymentsIcon />}
                                            disabled={paying || !acceptedBid.amount || !contract?.is_fully_signed}
                                            onClick={() => handlePay(acceptedBid.amount, latest.id)}
                                            fullWidth
                                        >
                                            پرداخت آنلاین
                                        </Button>
                                        {contract && !contract.is_fully_signed && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                پرداخت پس از پذیرش قرارداد توسط هر دو طرف فعال می‌شود.
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Paper>
                        ) : pendingBidsCount > 0 ? (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                {pendingBidsCount.toLocaleString('fa-IR')} پیشنهاد از همیاران دریافت کرده‌اید — برای مقایسه و پذیرش به «درخواست‌های من» بروید.
                            </Alert>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>در انتظار دریافت پیشنهاد از همیاران هستیم.</Alert>
                        )}

                        {payMessage && <Alert severity="warning" sx={{ mb: 2 }}>{payMessage}</Alert>}

                        <Button component={RouterLink} to="/my-requests" variant="outlined" size="small">مشاهده‌ی همه‌ی جزئیات</Button>
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 3 }}>
                        <SearchIcon sx={{ fontSize: 56, color: 'text.disabled' }} />
                        <Typography sx={{ mt: 2 }}>شما هیچ درخواست فعالی ندارید.</Typography>
                        <Button variant="contained" component={RouterLink} to="/create-request" sx={{ mt: 2 }}>
                            ثبت اولین درخواست
                        </Button>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Button fullWidth variant="outlined" size="large" component={RouterLink} to="/create-request" startIcon={<AddCircleOutlineIcon />}>
                    ثبت درخواست جدید
                </Button>
                <Button fullWidth variant="outlined" size="large" component={RouterLink} to="/my-requests" startIcon={<HistoryIcon />}>
                    تاریخچه‌ی درخواست‌ها ({(dashboardData?.completed_requests_count || 0).toLocaleString('fa-IR')})
                </Button>
            </Box>
        </Container>
        </Box>
    );
}

export default RequesterDashboard;
